import { BrowserStorage, root } from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';

import { Config } from '../domain/entities/Config';
import { ConfigMangerApiSdk } from '../../api/ConfigMangerApiSdk';
import { IConfigMapping } from '../domain/config-mappings/IConfigMapping';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '../../service-setup/ConfigManagerService';

const { name, version } = __serviceInfo__;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IConfig = Record<string, any>;

const configMap: IConfig = {
    isSaveForLaterFeatureEnabled: 'saveForLaterFeatureEnabled',
    itemMaxQuantity: 'item_max_quantity',
    orderMaxValue: 'order_max_value',
    orderSkuMaxLimit: 'order_sku_max_limit',
    currency: 'currency',
    noOrdersRedirectUrl: 'no_orders_redirect',
    features: 'features'
};

const TTL_MINUTES = 5 * 60000;

export class ConfigRepository {
    private configMangerApiSdk: ConfigMangerApiSdk;
    private ecommConfig: Config;
    public buidConfig: Config;
    private logger: ELCLogger;

    private configMapping: IConfigMapping;

    constructor() {
        this.configMangerApiSdk = diContainer.get(serviceNames.configManagerApiSdk);
        this.ecommConfig = Config.fromJSON({});
        this.buidConfig = Config.fromJSON({});
        this.setMapping({});
        this.logger = new ELCLogger({
            serviceName: name,
            environment: root.env,
            buid: root.buid,
            serviceVersion: version
        });
    }

    private get isCacheable(): boolean {
        try {
            return (
                window &&
                'sessionStorage' in window &&
                Object.getPrototypeOf(sessionStorage) === Storage.prototype
            );
        } catch (error) {
            return false;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private storeInCache(cacheKey: string, cacheData: any): void {
        // Prevents the storage of empty objects
        const shouldStoreData = Boolean(cacheData && Object.keys(cacheData).length);

        if (this.isCacheable && shouldStoreData) {
            BrowserStorage.setItem(cacheKey, JSON.stringify(cacheData), 'sessionStorage');
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private loadConfigFromCache(cacheKey: string): any {
        if (!this.isCacheable) {
            return false;
        }

        const cachedConfig = sessionStorage.getItem(cacheKey);

        if (cachedConfig) {
            const parsedConfig = JSON.parse(cachedConfig);
            const shouldReturnCachedValue = Boolean(
                parsedConfig && Object.keys(parsedConfig).length
            );

            if (shouldReturnCachedValue) {
                return parsedConfig;
            }
        }

        return false;
    }

    public loadConfigs = async (businessUnitId: string, refresh: boolean = false) => {
        let configs;
        const cacheKey = `buid-config-${businessUnitId}`;
        const cacheTtlKey = 'buid-set-time';
        let _refresh = refresh;
        const cachedConfig = this.loadConfigFromCache(cacheKey);

        const configCachedTime = sessionStorage.getItem(cacheTtlKey);
        const parsedConfigCacheTime = isNaN(parseInt(configCachedTime || ''))
            ? 0
            : parseInt(configCachedTime || '');
        // refresh data if cache time older than specified number of minutes
        if (parsedConfigCacheTime < Date.now() - TTL_MINUTES) {
            sessionStorage.setItem(cacheTtlKey, Date.now().toString());
            _refresh = true;
        }

        if (cachedConfig && !_refresh) {
            configs = cachedConfig;
        } else {
            configs = await this.configMangerApiSdk.getConfigByBusinessUnitId(businessUnitId);
            this.storeInCache(cacheKey, configs);
        }

        this.setBuidConfig(configs);
    };

    public setEcommConfig = (config: Config) => {
        this.ecommConfig = config;
        this.ecommConfig.setMapping(this.configMapping);
    };

    public setMapping(mapping: IConfigMapping) {
        this.configMapping = mapping;
        this.ecommConfig.setMapping(this.configMapping);
        this.buidConfig.setMapping(this.configMapping);
    }

    public getConfigs = async (payload: string[]) => {
        return payload.reduce((acc, configKey) => {
            const key = configMap[configKey] || configKey;
            let configValue: IConfig | string = '';

            if (key === 'ecommConfig') {
                configValue = this.ecommConfig.config;
            } else {
                configValue = this.buidConfig.getConfigByName(key);
                if (configValue === '') {
                    configValue = this.ecommConfig.getConfigByName(key);
                }
            }

            if (configValue === '') {
                const error = new Error(`Config key ${configKey} not found`);
                this.logger.warning({
                    message: error.message,
                    triggerType: TriggerType.config,
                    payload: {
                        error
                    }
                });
            }

            return { ...acc, [configKey]: configValue };
        }, {});
    };

    private setBuidConfig = (configs: IConfig) => {
        this.buidConfig = Config.fromJSON(configs);
        this.buidConfig.setMapping(this.configMapping);
    };
}
